import React, { useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import useAsync from 'components/hooks/useAsync';
import { useQuery } from 'components/hooks/useQuery';
import { useHistory } from 'react-router-dom';
import { useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { checkUserIsReviewAdmin } from 'utils/helpers/rolesAccess';

import { NeedAttention, Plus } from 'assets/assetsV1/iconComponents';
import addReviewV1 from 'assets/assetsV1/illustrations/newIllustrations/addReviewV1.svg';
import noReviewsPendingv1 from 'assets/assetsV1/illustrations/newIllustrations/noReviewsPendingv1.svg';

import { goToReviewCycle } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';

import { getReviewHelpTimeline } from 'services/performanceReview';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper, PaperWithShadow } from 'uiToolkitV1/Paper';
import { ReviewTimeline } from 'uiToolkitV1/ReviewTimeline';
import { TextLabel } from 'uiToolkitV1/Typography';

import { showLoader } from '../components/common';
import PerformanceReviewGraphV1 from './PerformanceReviewGraphV1';
import ReviewsCompletedByMe from './ReviewsCompletedByMe';
import ReviewsPast from './ReviewsPast';
import ReviewsPendingOnMe from './ReviewsPendingOnMe';
import ReviewsPendingOnOthers from './ReviewsPendingOnOthers';
import useReviewSectionsData from './hooks/dataHooks';

export const REVIEW_TABLE_TYPES = {
  PENDING_ON_ME: 'PENDING_ON_ME',
  PENDING_ON_OTHERS: 'PENDING_ON_OTHERS',
  COMPLETED_BY_ME: 'COMPLETED_BY_ME',
  MY_PAST_REVIEWS: 'MY_PAST_REVIEWS',
};

const TABLE_TYPES_COMP = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: ReviewsPendingOnMe,
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: ReviewsPendingOnOthers,
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: ReviewsCompletedByMe,
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: ReviewsPast,
};

export const TABLE_TYPES_EVENT_VALUE_NAME = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: 'PendingOnMe',
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: 'PendingOnOthers',
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: 'CompletedByMe',
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: 'PastReviews',
};

export const TABLE_TYPES_EVENT_INTENT_VALUE_NAME = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: 'Me-reviews-pending on me',
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: 'Me-reviews-pending on others',
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: 'Me-reviews-completed by me',
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: 'Me-reviews-past results',
};

const ReviewInstanceTimeline = ({ reviewTimelineData }) => {
  if (!reviewTimelineData || reviewTimelineData?.length === 0) return null;

  return (
    <Flex mb={3} width="100%">
      <ReviewTimeline timelineData={reviewTimelineData} />
    </Flex>
  );
};

const getReviewLandingPageRoute = () =>
  checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.audit;

export const getSelectedReviewSectionData = ({ reviewsSections, selectedTableId }) =>
  reviewsSections.filter(({ sectionId }) => sectionId === selectedTableId)?.[0]?.sectionData ?? null;

const getRouteHelper = ({ activeTableId, selectedReviewCycleId }) =>
  `${getReviewLandingPageRoute()}?activeTableId=${activeTableId}&reviewCycleId=${selectedReviewCycleId}`;

const testReviewCycleAndInstanceId = ({ reviewCycleId, reviewSectionData }) => {
  if (reviewCycleId === null || Number.isNaN(reviewCycleId)) return true;
  const matchedReviewCycle = reviewSectionData?.reviewCycles?.filter(({ id }) => id === reviewCycleId)[0];
  if (!matchedReviewCycle) {
    return false;
  }
  return true;
};

const checkForReviewCycleExistenceForPastReviews = (sectionData = []) => {
  const derivedSectionData = sectionData ?? [];

  return (
    (derivedSectionData ?? []).filter(({ sectionId }) => sectionId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS)?.[0]
      ?.sectionData?.pastReviewCycleCount ?? 0 >= 1
  );
};

const checkForReviewCycleExistence = (sectionData = []) => {
  const derivedSectionData = sectionData ?? [];

  // this checks for first three sections
  const count = derivedSectionData.reduce((acc, curr) => acc + (curr?.sectionData?.reviewCycles?.length ?? 0), 0);

  if (count) return !!count;
  // else check separately for past review as it has different structure
  return checkForReviewCycleExistenceForPastReviews(derivedSectionData);
};

const NoDataComp = ({ heading, description, illustrationSrc, CTAComp }) => (
  <Paper px={6} py={2}>
    <div style={{ marginTop: '60px' }}>
      <EmptyScreen heading={heading} description={description} illustrationSrc={illustrationSrc} CTAComp={CTAComp} />
    </div>
  </Paper>
);

const ReviewTables = ({ gridRef }) => {
  const { activeTableId = '', reviewCycleId = '' } = useQuery();
  const { selectedReviewCycle: reduxSelectedReviewCycle } = useReviewManageSidepanelData();

  // 'null' string(string because it's read from query param) is used to represent the first option in the dropdown i.e. 'all review cycles'
  const selectedReviewCycleId = ['', 'null'].includes(reviewCycleId)
    ? reduxSelectedReviewCycle?.id || null
    : Number(reviewCycleId);

  const selectedTableId = activeTableId !== '' ? activeTableId : null;
  const history = useHistory();

  const { data: reviewsSectionsData, isLoading: isLoadingReviewSectionsData } = useReviewSectionsData();

  const { data: reviewTimelineData, run: timelineRun } = useAsync();

  const hasReviewCycles = checkForReviewCycleExistence(reviewsSectionsData);
  const isSelectedSectionHasReviewCycle =
    selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS
      ? checkForReviewCycleExistenceForPastReviews(reviewsSectionsData)
      : ((reviewsSectionsData ?? []).filter(({ sectionId }) => sectionId === selectedTableId)?.[0]?.sectionData
          ?.reviewCycles?.length ?? 0 >= 1);

  const reviewsSections = useMemo(() => reviewsSectionsData ?? [], [reviewsSectionsData]);
  //
  const TableComp = TABLE_TYPES_COMP?.[selectedTableId] ?? (() => null);

  const selectedReviewSectionData = getSelectedReviewSectionData({
    reviewsSections,
    selectedTableId,
  });
  const selectedReviewCycleData = selectedReviewSectionData?.reviewCycles?.filter(
    ({ id }) => id === selectedReviewCycleId
  )[0];
  const { latestFeedbackFormId, lastReviewCycleName } = selectedReviewCycleData ?? {};

  useEffect(() => {
    if (selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS || selectedReviewCycleData?.reviewType === 'TRIGGERED') {
      return;
    }

    if (latestFeedbackFormId) {
      timelineRun(getReviewHelpTimeline({ reviewId: latestFeedbackFormId }));
    }
  }, [timelineRun, selectedTableId, selectedReviewCycleData, latestFeedbackFormId]);

  useEffect(() => {
    if (!selectedTableId && reviewsSections.length === 0) {
      history.push(
        getRouteHelper({
          activeTableId: REVIEW_TABLE_TYPES.PENDING_ON_ME,
          selectedReviewCycleId: null,
        })
      );
    }

    if (selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS) {
      return;
    }

    if (reviewsSections.length > 0) {
      const isValidTableReviewCycleCombination =
        selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS ||
        testReviewCycleAndInstanceId({
          reviewCycleId: selectedReviewCycleId,
          reviewSectionData: selectedReviewSectionData,
        });

      if (!selectedTableId || !isValidTableReviewCycleCombination) {
        const firstReviewCycleData = getSelectedReviewSectionData({
          reviewsSections,
          selectedTableId: selectedTableId || reviewsSections[0].sectionId,
        }).reviewCycles[0];

        history.replace(
          getRouteHelper({
            activeTableId: selectedTableId || reviewsSections[0]?.sectionId,
            selectedReviewCycleId: firstReviewCycleData?.id,
          })
        );
      }
    }
  }, [history, reviewsSections, selectedReviewCycleId, selectedReviewSectionData, selectedTableId]);

  // check if the review cycle id and instance id exist in specified table or not
  const isValidTableReviewCycleCombination =
    selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS ||
    testReviewCycleAndInstanceId({
      reviewCycleId: selectedReviewCycleId,
      reviewSectionData: selectedReviewSectionData,
    });

  const createNewReviewCycle = () => {
    goToReviewCycle({ history, entityId: 'new' });
  };

  const addReviewCycle = () => (
    <Button onClick={createNewReviewCycle} StartIcon={Plus}>
      Create New Cycle
    </Button>
  );

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: TABLE_TYPES_EVENT_INTENT_VALUE_NAME[selectedTableId],
      },
    });
  }, [selectedTableId]);

  if (isLoadingReviewSectionsData) {
    return (
      <PaperWithShadow px={6} py={2}>
        {showLoader()}
      </PaperWithShadow>
    );
  }
  if (!hasReviewCycles) {
    return (
      <PaperWithShadow p={3}>
        <div style={{ marginTop: '60px' }}>
          {checkUserIsReviewAdmin() ? (
            <EmptyScreen
              heading="You will find updates here once you launch a performance review cycle."
              illustrationSrc={addReviewV1}
              CTAComp={addReviewCycle}
            />
          ) : (
            <EmptyScreen
              heading="You will find updates here once your admin launches a performance review cycle."
              illustrationSrc={addReviewV1}
            />
          )}
        </div>
      </PaperWithShadow>
    );
  }

  return (
    <>
      {isValidTableReviewCycleCombination &&
        Boolean(isSelectedSectionHasReviewCycle) &&
        selectedReviewCycleId !== '' &&
        selectedReviewCycleData?.reviewType !== 'TRIGGERED' &&
        !isMobileView() &&
        selectedTableId &&
        selectedTableId !== REVIEW_TABLE_TYPES.MY_PAST_REVIEWS && (
          <PaperWithShadow p={3} mb={3}>
            <ReviewInstanceTimeline reviewTimelineData={reviewTimelineData} />
            {lastReviewCycleName && !isMobileView() && (
              <Flex sx={{ mt: -2 }} alignItems="center">
                <NeedAttention color="text.g2" boxSize="13px" />
                <TextLabel sx={{ ml: 2 }}>Displaying the timeline of the review cycle: {lastReviewCycleName}</TextLabel>
              </Flex>
            )}
          </PaperWithShadow>
        )}
      <PaperWithShadow p={3}>
        {!isSelectedSectionHasReviewCycle &&
          selectedTableId === 'PENDING_ON_ME' &&
          (checkUserIsReviewAdmin() ? (
            <EmptyScreen
              illustrationSrc={addReviewV1}
              heading="Clean slate ahead!"
              description="There are no pending reviews for you. Swing by later when new cycles kick off, or there are fresh reviews to fill."
              CTAComp={addReviewCycle}
            />
          ) : (
            <NoDataComp
              illustrationSrc={noReviewsPendingv1}
              heading="Clean slate ahead!"
              description="There are no pending reviews for you. Swing by later when new cycles kick off, or there are fresh reviews to fill."
            />
          ))}
        {!isSelectedSectionHasReviewCycle &&
          selectedTableId === 'PENDING_ON_OTHERS' &&
          (checkUserIsReviewAdmin() ? (
            <EmptyScreen
              illustrationSrc={addReviewV1}
              heading="Clean slate ahead!"
              description="Your colleagues don't have any pending reviews for you. Stop by later when new cycles begin, or there are new reviews to complete."
              CTAComp={addReviewCycle}
            />
          ) : (
            <NoDataComp
              illustrationSrc={noReviewsPendingv1}
              heading="Clean slate ahead!"
              description="Your colleagues don't have any pending reviews for you. Stop by later when new cycles begin, or there are new reviews to complete."
            />
          ))}
        {!isSelectedSectionHasReviewCycle && selectedTableId === 'COMPLETED_BY_ME' && (
          <EmptyScreen
            heading="Poof! Nothing here."
            illustrationSrc={noReviewsPendingv1}
            description="You can track the status of all the completed reviews across review cycles here."
          />
        )}
        {!isSelectedSectionHasReviewCycle && selectedTableId === 'MY_PAST_REVIEWS' && (
          <EmptyScreen
            heading="Poof! Nothing here."
            illustrationSrc={noReviewsPendingv1}
            description="You can track the status of all the past reviews across review cycles here."
          />
        )}
        {isValidTableReviewCycleCombination && Boolean(isSelectedSectionHasReviewCycle) && (
          <Box minH="200px">
            <TableComp
              selectedTableRef={gridRef}
              selectedTableId={selectedTableId}
              selectedReviewCycleId={selectedReviewCycleId}
            />
          </Box>
        )}
      </PaperWithShadow>
    </>
  );
};

const ReviewAuditIntent = ({ gridRef }) => (
  <Box>
    <Box p={checkNewNavEnabled() ? 0 : 4}>
      {!isMobileView() && (
        <Flex mb={3} gap={4} h="348px">
          <PaperWithShadow w="100%" py={0} h="100%">
            <PerformanceReviewGraphV1 emptyMessage="During active review cycles, you can view your performance growth curve here." />
          </PaperWithShadow>
        </Flex>
      )}

      <ReviewTables gridRef={gridRef} />
    </Box>
  </Box>
);

export { ReviewAuditIntent };
